import React, { useCallback, useContext, useMemo } from 'react';

import Dropdown from 'components/Inputs/Dropdown';
import Row from 'components/Common/Row';
import Tooltip from 'components/Tooltip';
import { WeeklyBonusOverrideContext } from 'components/DriverAX/providers/WeeklyBonusOverrideProvider';
import useCanOverrideCategory from 'components/DriverAX/hooks/useCanOverrideCategory';
import useRewardOverrideDropdown from 'components/DriverAX/components/useRewardOverrideDropdown';
import getRewardUnits from 'components/DriverAX/utils/getRewardUnits';
import useValueFormatters from 'hooks/useValueFormatters';

import WeeklyClearOverrideControl from './WeeklyClearOverrideControl';

/**
 * A single week's payout-override control inside the sub-period fact table. Shown
 * in override mode in place of the plain payout figure for the reward the pay
 * column maps to. Reads its week's options / staged / persisted state from
 * WeeklyBonusOverrideContext and stages a per-week reward override; the confirm
 * fans out one PUT per changed week. Falls back to the plain figure when the week
 * isn't overridable.
 */
const WeeklyRewardOverrideCell = ({
  bonusDatasetRecordId,
  rewardName,
  field,
  bonusProgram,
  label,
}: {
  bonusDatasetRecordId: string;
  rewardName: string;
  // The pay column this cell sits in (e.g. `safetyBonusPay`) — its own formatter
  // drives the option labels so they read like the rest of the column.
  field: string;
  bonusProgram: BonusTypes.BonusProgram;
  // The plain formatted figure for this week's pay field — the collapsed label
  // when nothing is staged and the fallback when the week isn't overridable.
  label: string;
}) => {
  const { byRecordId, stageWeekReward } = useContext(
    WeeklyBonusOverrideContext,
  );
  const { formatField } = useValueFormatters();
  const week = byRecordId.get(bonusDatasetRecordId);

  // Format option values with the pay column's own field format (currency for
  // `*Pay`, plain for `*Points`), so a monetary twin on a points program reads $.
  const formatRewardValueOverride = useCallback(
    (value: number): string =>
      String(formatField({ value, field, dataset: bonusProgram.dataset })),
    [formatField, field, bonusProgram.dataset],
  );

  const { canOverride } = useCanOverrideCategory({
    bonusProgramId: bonusProgram.id,
    category: rewardName,
  });

  const units = useMemo(
    () => getRewardUnits({ bonusProgram, rewardName }),
    [bonusProgram, rewardName],
  );

  const reward = useMemo((): BonusTypes.Rewards.AnyRewardOutput | undefined => {
    const rewards = week?.actual.rewards ?? [];
    const topLevel = rewards.find((r) => r.name === rewardName);
    if (topLevel) {
      return topLevel;
    }
    for (const r of rewards) {
      const sub = (r.rewards ?? []).find((s) => s.name === rewardName);
      if (sub) {
        return sub;
      }
    }
    return undefined;
  }, [week?.actual.rewards, rewardName]);

  const optionsEntry = week?.options.get(rewardName);

  const persistedOverride = useMemo(
    () =>
      week?.actual.rewardOverrides?.find((o) => o.rewardName === rewardName),
    [week?.actual.rewardOverrides, rewardName],
  );

  const stagedOverride = useMemo(
    () => week?.staged.find((o) => o.rewardName === rewardName),
    [week?.staged, rewardName],
  );

  const stageRewardOverride = useCallback(
    (override: BonusTypes.Overrides.UserInputRewardOverride | undefined) => {
      stageWeekReward({ bonusDatasetRecordId, rewardName, override });
    },
    [stageWeekReward, bonusDatasetRecordId, rewardName],
  );

  const { options, renderSelectedLabel } = useRewardOverrideDropdown({
    rewardName,
    rewardType: bonusProgram.rewardType,
    units,
    dataset: bonusProgram.dataset,
    reward,
    optionsEntry,
    hasPersistedOverride: !!persistedOverride,
    stagedOverride,
    stageRewardOverride,
    label,
    formatRewardValueOverride,
  });

  // No record / no options for this week → plain figure (not overridable).
  if (!week || !optionsEntry) {
    return <>{label}</>;
  }

  // Can't override this week (driver disqualified / not the board owner): show the
  // dropdown disabled rather than a plain grey figure, so it still reads as a
  // control, with the reason on hover. A staged eligibility override counts —
  // setting the week to Qualified here unlocks its pay dropdowns.
  if (!week.isEffectivelyQualified || !canOverride) {
    return (
      <Tooltip
        content={
          !week.isEffectivelyQualified
            ? 'You cannot override as the driver is disqualified'
            : 'You cannot override as you are not the board owner'
        }
      >
        <Dropdown
          options={options}
          renderSelectedLabel={renderSelectedLabel}
          isDisabled
          unsetWidth
          size={'Small'}
          testId={`weekly-reward-override-${bonusDatasetRecordId}-${rewardName}-dropdown`}
        />
      </Tooltip>
    );
  }

  return (
    <Row centerAlign style={{ gap: 4, justifyContent: 'flex-end' }}>
      <Dropdown
        options={options}
        renderSelectedLabel={renderSelectedLabel}
        isDisabled={week.isPreviewLoading}
        unsetWidth
        size={'Small'}
        testId={`weekly-reward-override-${bonusDatasetRecordId}-${rewardName}-dropdown`}
      />
      {!!persistedOverride && (
        <WeeklyClearOverrideControl
          isCleared={stagedOverride?.action === 'Clear'}
          onClear={() => stageRewardOverride({ action: 'Clear', rewardName })}
          onUndo={() => stageRewardOverride(undefined)}
          testId={`weekly-reward-clear-${bonusDatasetRecordId}-${rewardName}`}
        />
      )}
    </Row>
  );
};

export default WeeklyRewardOverrideCell;
